import { formatMoney } from '@/utils/format';
import { cardLast4, formatCardNumber, formatExpiry, type CardFormState } from './cardForm';

interface TopUpCardFormProps {
  card: CardFormState;
  onChange: (patch: Partial<CardFormState>) => void;
  currency: string;
  amount: number;
}

const fieldClass =
  'h-10 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-400/30';

export default function TopUpCardForm({ card, onChange, currency, amount }: TopUpCardFormProps) {
  const last4 = cardLast4(card);
  const displayNumber = card.number || '•••• •••• •••• ••••';

  return (
    <div className="space-y-4">
      <div className="relative overflow-hidden rounded-lg border border-foreground-800 bg-foreground-950 px-5 py-4 text-background-50">
        <div className="flex items-center justify-between">
          <span className="w-8 h-8 flex items-center justify-center rounded-md bg-background-50/10 text-lg">
            <i className="ri-bank-card-line" />
          </span>
          <span className="text-[11px] font-label uppercase tracking-wider text-background-50/60">
            Secure payment
          </span>
        </div>
        <p className="mt-4 font-label text-lg tracking-widest text-background-50 tabular-nums">
          {displayNumber}
        </p>
        <div className="mt-4 flex items-end justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wider text-background-50/50">
              Card holder
            </p>
            <p className="truncate text-sm text-background-50/90">
              {card.name.trim() || 'Your name'}
            </p>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-wider text-background-50/50">Expires</p>
            <p className="text-sm text-background-50/90 tabular-nums">
              {card.expiry || 'MM/YY'}
            </p>
          </div>
        </div>
      </div>

      <div>
        <label
          htmlFor="card-number"
          className="mb-1.5 block text-xs font-label font-medium text-foreground-700"
        >
          Card number
        </label>
        <div className="relative">
          <input
            id="card-number"
            inputMode="numeric"
            autoComplete="cc-number"
            value={card.number}
            onChange={(event) => onChange({ number: formatCardNumber(event.target.value) })}
            placeholder="1234 5678 9012 3456"
            className={`${fieldClass} pr-10 tabular-nums`}
          />
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-foreground-500">
            <i className={last4 ? 'ri-bank-card-fill' : 'ri-bank-card-line'} />
          </span>
        </div>
      </div>

      <div>
        <label
          htmlFor="card-name"
          className="mb-1.5 block text-xs font-label font-medium text-foreground-700"
        >
          Name on card
        </label>
        <input
          id="card-name"
          autoComplete="cc-name"
          value={card.name}
          onChange={(event) => onChange({ name: event.target.value })}
          placeholder="As printed on the card"
          className={fieldClass}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label
            htmlFor="card-expiry"
            className="mb-1.5 block text-xs font-label font-medium text-foreground-700"
          >
            Expiry
          </label>
          <input
            id="card-expiry"
            inputMode="numeric"
            autoComplete="cc-exp"
            value={card.expiry}
            onChange={(event) => onChange({ expiry: formatExpiry(event.target.value) })}
            placeholder="MM/YY"
            className={`${fieldClass} tabular-nums`}
          />
        </div>
        <div>
          <label
            htmlFor="card-cvc"
            className="mb-1.5 block text-xs font-label font-medium text-foreground-700"
          >
            CVC
          </label>
          <input
            id="card-cvc"
            inputMode="numeric"
            autoComplete="cc-csc"
            value={card.cvc}
            onChange={(event) =>
              onChange({ cvc: event.target.value.replace(/[^0-9]/g, '').slice(0, 4) })
            }
            placeholder="123"
            className={`${fieldClass} tabular-nums`}
          />
        </div>
      </div>

      <p className="flex items-start gap-2 rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-[11px] leading-relaxed text-foreground-600">
        <span className="mt-0.5 w-3.5 h-3.5 flex items-center justify-center shrink-0">
          <i className="ri-lock-2-line" />
        </span>
        You'll be charged {formatMoney(amount, currency)}. Card details are used for this top-up
        only and are never stored.
      </p>
    </div>
  );
}